import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAgency, yearOf } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import { formatRSD, fmtDate } from '../lib/helpers';
import Icon from './Icon';

// ============================================================
// Dnevni pazar — za delatnosti bez faktura (taksisti, saloni, ordinacije sa drugom kasom…)
// Svaki unos se čuva kao dokument vrste "pazar" (naplaćen), pa sam ulazi u KPO knjigu i u oba limita.
// ============================================================
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const parseAmount = (v) => Number(String(v || '').replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '')) || 0;

export default function DailyTakings() {
  const { activeAgencyId, year } = useAgency();
  const { tr } = useI18n();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ date: today(), amount: '', revenue_kind: 'usluga', method: 'gotovina', note: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    if (!activeAgencyId) return;
    const { data } = await supabase.from('invoices').select('*').eq('agency_id', activeAgencyId).eq('document_kind', 'pazar').order('issue_date', { ascending: false });
    setRows((data || []).filter((r) => yearOf(r.issue_date) === year));
  }, [activeAgencyId, year]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const amount = parseAmount(form.amount);
    if (!(amount > 0)) { setMsg({ ok: '', err: tr('Upišite iznos pazara.', 'Enter the amount.', 'Введите сумму.') }); return; }
    if (!form.date) { setMsg({ ok: '', err: tr('Izaberite datum.', 'Choose a date.', 'Выберите дату.') }); return; }
    setBusy(true); setMsg({ ok: '', err: '' });
    const [y, m, d] = form.date.split('-');
    const methodLabel = { gotovina: 'gotovina', kartica: 'kartica', prenos: 'prenos na račun' }[form.method];
    const row = {
      agency_id: activeAgencyId,
      document_kind: 'pazar',
      type: 'domaca',
      currency: 'RSD',
      amount,
      issue_date: form.date,
      service_date: form.date,
      status: 'naplaceno',
      paid_amount: amount,
      paid_at: form.date,
      payment_method: form.method,
      revenue_kind: form.revenue_kind === 'proizvod' ? 'proizvod' : null,
      invoice_number: `Pazar ${d}.${m}.${y}.`,
      client: `Dnevni pazar (${methodLabel})`,
      description: form.note.trim() || 'Dnevni pazar',
    };
    let { error } = await supabase.from('invoices').insert(row);
    // Ako baza nema neku od dodatnih kolona, sačuvaj bez nje
    if (error && /payment_method|revenue_kind/.test(error.message || '')) {
      const { payment_method, revenue_kind, ...rest } = row; // eslint-disable-line no-unused-vars
      ({ error } = await supabase.from('invoices').insert(rest));
    }
    setBusy(false);
    if (error) { setMsg({ ok: '', err: error.message }); return; }
    setMsg({ ok: tr(`Upisano ${formatRSD(amount)} za ${fmtDate(form.date)} — u KPO knjizi i u limitima.`, `Saved ${formatRSD(amount)} for ${fmtDate(form.date)} — in the KPO book and limits.`, `Записано ${formatRSD(amount)}.`), err: '' });
    setForm((f) => ({ ...f, amount: '', note: '' }));
    load();
  };

  const remove = async (id) => {
    const { error } = await supabase.from('invoices').delete().eq('id', id);
    setDelId(null);
    if (error) { setMsg({ ok: '', err: error.message }); return; }
    load();
  };

  const nowMonth = today().slice(0, 7);
  const sumMonth = useMemo(() => rows.filter((r) => String(r.issue_date).slice(0, 7) === nowMonth).reduce((s, r) => s + Number(r.amount || 0), 0), [rows, nowMonth]);
  const sumYear = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  return (
    <div style={s.page}>
      <div>
        <div className="mono-label">{tr('Prodaja', 'Sales', 'Продажи')}</div>
        <h1 style={s.heading}>{tr('Dnevni pazar', 'Daily takings', 'Дневная выручка')}</h1>
      </div>

      <div style={s.stats}>
        <section style={s.stat} className="glow-card">
          <span className="mono-label">{tr('Ovaj mesec', 'This month', 'Этот месяц')}</span>
          <span style={s.statValue}>{formatRSD(sumMonth)}</span>
        </section>
        <section style={s.stat}>
          <span className="mono-label">{tr(`Ukupno ${year}.`, `Total ${year}`, `Всего ${year}`)}</span>
          <span style={s.statValue}>{formatRSD(sumYear)}</span>
        </section>
      </div>

      {/* Unos */}
      <section style={s.card}>
        <div style={s.cardHead}>
          <span style={s.cardIcon}><Icon name="plus" size={15} color="var(--lavender-700)" /></span>
          <h2 style={s.cardTitle}>{tr('Upiši pazar', 'Add takings', 'Добавить выручку')}</h2>
        </div>
        <p style={s.note}>{tr('Za delatnosti bez faktura (npr. taksi, saloni, ordinacije koje račune kucaju na drugoj kasi). Iznos ulazi u KPO knjigu i u oba limita, isto kao izdata faktura.',
          'For businesses without invoices. The amount goes into the KPO book and both limits, like an issued invoice.',
          'Для деятельности без счетов. Сумма попадает в КПО и в лимиты.')}</p>
        <div style={s.grid}>
          <label><span style={s.label}>{tr('Datum', 'Date', 'Дата')}</span><input type="date" value={form.date} onChange={set('date')} style={s.input} /></label>
          <label><span style={s.label}>{tr('Iznos (RSD)', 'Amount (RSD)', 'Сумма (RSD)')}</span><input inputMode="decimal" value={form.amount} onChange={set('amount')} placeholder="21.000,00" style={{ ...s.input, fontVariantNumeric: 'tabular-nums' }} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} /></label>
          <label><span style={s.label}>{tr('Vrsta prihoda (KPO)', 'Revenue type (KPO)', 'Тип дохода')}</span>
            <select value={form.revenue_kind} onChange={set('revenue_kind')} style={s.input}>
              <option value="usluga">{tr('Usluge (kolona 4)', 'Services (col. 4)', 'Услуги (4)')}</option>
              <option value="proizvod">{tr('Prodaja proizvoda (kolona 3)', 'Product sales (col. 3)', 'Товары (3)')}</option>
            </select>
          </label>
          <label><span style={s.label}>{tr('Način naplate', 'Payment method', 'Способ оплаты')}</span>
            <select value={form.method} onChange={set('method')} style={s.input}>
              <option value="gotovina">{tr('Gotovina', 'Cash', 'Наличные')}</option>
              <option value="kartica">{tr('Kartica', 'Card', 'Карта')}</option>
              <option value="prenos">{tr('Prenos na račun', 'Bank transfer', 'Перевод')}</option>
            </select>
          </label>
        </div>
        <label><span style={s.label}>{tr('Napomena (nije obavezno)', 'Note (optional)', 'Примечание')}</span><input value={form.note} onChange={set('note')} style={s.input} placeholder={tr('npr. vožnje 3.11.', 'e.g. rides on 3 Nov', 'напр.')} /></label>
        <div><button onClick={save} disabled={busy} style={{ ...s.primary, ...(busy ? { opacity: 0.6 } : {}) }}><Icon name="check" size={14} />{busy ? tr('Upisujem…', 'Saving…', 'Сохранение…') : tr('Upiši pazar', 'Save', 'Сохранить')}</button></div>
        {msg.ok && <div style={s.ok}>{msg.ok}</div>}
        {msg.err && <div style={s.err}>{msg.err}</div>}
      </section>

      {/* Spisak */}
      <section style={s.card}>
        <div style={s.cardHead}>
          <span style={s.cardIcon}><Icon name="book" size={15} color="var(--lavender-700)" /></span>
          <h2 style={s.cardTitle}>{tr(`Upisani pazari ${year}.`, `Entries ${year}`, `Записи ${year}`)}</h2>
          <span style={s.count}>{rows.length}</span>
        </div>
        {rows.length === 0 ? <div style={s.empty}>{tr('Još nema upisanog pazara.', 'No entries yet.', 'Записей пока нет.')}</div> : (
          <div style={s.list}>
            {rows.map((r) => (
              <div key={r.id} style={s.row}>
                <span style={s.date}>{fmtDate(r.issue_date)}</span>
                <span style={s.desc}>{r.description}{r.client ? <span style={s.sub}> · {String(r.client).replace(/^Dnevni pazar \(|\)$/g, '')}</span> : null}</span>
                <span style={s.amount}>{formatRSD(r.amount)}</span>
                {delId === r.id ? (
                  <span style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => remove(r.id)} style={s.dangerBtn}>{tr('Obriši', 'Delete', 'Удалить')}</button>
                    <button onClick={() => setDelId(null)} style={s.iconBtn} aria-label={tr('Odustani', 'Cancel', 'Отмена')}><Icon name="x" size={13} /></button>
                  </span>
                ) : (
                  <button onClick={() => setDelId(r.id)} style={s.iconBtn} aria-label={tr('Obriši', 'Delete', 'Удалить')}><Icon name="trash" size={13} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const s = {
  page: { display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 900, margin: '0 auto' },
  heading: { fontSize: 'var(--fs-page, 22px)', fontWeight: 700, margin: '4px 0 0' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
  stat: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 },
  statValue: { fontSize: 'var(--fs-number, 20px)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', position: 'relative' },
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10 },
  cardIcon: { width: 28, height: 28, borderRadius: 8, background: 'var(--lavender-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardTitle: { fontSize: 14, fontWeight: 600, margin: 0, flex: 1 },
  count: { fontSize: 11, color: 'var(--ink-500)', background: 'var(--paper)', borderRadius: 6, padding: '1px 7px' },
  note: { margin: 0, fontSize: 12.5, color: 'var(--ink-500)', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 },
  label: { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 5 },
  input: { width: '100%', height: 38, border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px', fontSize: 14, background: 'var(--surface)' },
  primary: { display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 34, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  ok: { fontSize: 13, background: 'var(--lavender-100)', color: 'var(--ink-900)', borderRadius: 8, padding: '8px 10px' },
  err: { fontSize: 12.5, background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 8, padding: '8px 10px' },
  empty: { fontSize: 13, color: 'var(--ink-500)', padding: '6px 0' },
  list: { display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--line)' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--line)' },
  date: { fontSize: 13, fontWeight: 600, minWidth: 92, fontVariantNumeric: 'tabular-nums' },
  desc: { flex: 1, minWidth: 0, fontSize: 13, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { color: 'var(--ink-500)' },
  amount: { fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 110, textAlign: 'right' },
  iconBtn: { width: 30, height: 30, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-700)', cursor: 'pointer' },
  dangerBtn: { minHeight: 30, border: 'none', background: 'var(--red-600)', color: '#fff', borderRadius: 8, padding: '0 10px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' },
};
